import { passwordSchema } from '@/api/auth/auth.schema';
import { TextField } from '@/components/enc/fields';
import { nameInitials } from '@/components/enc/format';
import { InitialsAvatar, PageHeader, Panel } from '@/components/enc/primitives';
import { Button } from '@/components/ui/button';
import { ContentLoader } from '@/components/ui/loaders';
import { useToast } from '@/hooks/use-toast';
import { useUpload } from '@/hooks/use-upload';
import { cn } from '@/lib/utils';
import { useTheme } from '@/providers/theme/use-theme';
import {
  useChangePassword,
  useCurrentUser,
  useSignOut,
  useUpdateMe,
} from '@/services/auth';
import { Role } from 'generated/prisma/browser';
import { Camera, Check, LayoutGrid, LogOut, Mail, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const PASSWORD_RULES: [string, (v: string) => boolean][] = [
  ['Pelo menos 8 caracteres', (v) => v.length >= 8],
  ['Uma letra maiúscula', (v) => /[A-Z]/.test(v)],
  ['Um número', (v) => /[0-9]/.test(v)],
  ['Um símbolo (!@#...)', (v) => /[^A-Za-z0-9]/.test(v)],
];

const STRENGTH: { label: string; color: string }[] = [
  { label: '—', color: 'var(--muted)' },
  { label: 'Fraca', color: '#E5484D' },
  { label: 'Razoável', color: 'var(--warn)' },
  { label: 'Boa', color: 'var(--ok)' },
  { label: 'Forte', color: 'var(--ok)' },
];

function SectionTitle({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-bold tracking-[.09em] text-brand">{eyebrow}</span>
      <span className="font-display text-xl font-bold text-ink">{title}</span>
      <span className="text-sm text-muted">{description}</span>
    </div>
  );
}

function Fact({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 border-b border-line px-6 py-3.5">
      <span className="flex size-8 flex-none items-center justify-center rounded-[9px] border border-line-2 text-brand [&_svg]:size-4">
        {icon}
      </span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[10px] font-bold tracking-[.1em] text-muted">{label}</span>
        <span className="text-sm font-semibold break-words text-ink">{value}</span>
      </span>
    </div>
  );
}

/** Foto ou iniciais, com botão para trocar a foto (envia e salva na hora). */
function ProfileAvatar({ name, avatar }: { name: string; avatar?: string | null }) {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const { mutate: upload, isPending: isUploading } = useUpload();
  const { mutate: updateMe, isPending: isSaving } = useUpdateMe();
  const busy = isUploading || isSaving;

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    upload(file, {
      onSuccess: (path) =>
        updateMe(
          { avatar: path },
          {
            onSuccess: () => toast.success('Foto atualizada.'),
            onError: (err) => {
              setPreview(null);
              toast.error(err.message);
            },
          },
        ),
      onError: () => setPreview(null),
    });
  };

  const src = preview ?? avatar;

  return (
    <div className="relative">
      <InitialsAvatar
        text={nameInitials(name)}
        src={src}
        className="size-[88px] border-4 border-surface text-[30px] font-extrabold tracking-[-0.02em]"
      />
      <button
        type="button"
        title="Trocar foto"
        aria-label="Trocar foto"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="absolute right-0 bottom-0 flex size-8 items-center justify-center rounded-full border-2 border-surface bg-brand-fill text-white transition-opacity hover:opacity-90 disabled:opacity-60"
      >
        {busy ? (
          <span className="size-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white" />
        ) : (
          <Camera className="size-4" />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="hidden"
      />
    </div>
  );
}

function AccountForm({ name, email }: { name: string; email: string }) {
  const toast = useToast();
  const { mutate: updateMe, isPending } = useUpdateMe();
  const [draft, setDraft] = useState({ name, email });
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  useEffect(() => setDraft({ name, email }), [name, email]);

  const dirty = draft.name !== name || draft.email !== email;

  const change = (field: 'name' | 'email', value: string) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    const next = {
      name: draft.name.trim() ? undefined : 'Campo obrigatório.',
      email: EMAIL_RE.test(draft.email.trim()) ? undefined : 'Informe um e-mail válido.',
    };
    setErrors(next);
    if (next.name || next.email) return;

    updateMe(
      { name: draft.name.trim(), email: draft.email.trim() },
      {
        onSuccess: () => toast.success('Perfil atualizado.'),
        onError: (err) =>
          err.message?.includes('e-mail')
            ? setErrors({ email: err.message })
            : toast.error(err.message),
      },
    );
  };

  return (
    <Panel className="flex flex-col gap-[22px] p-7">
      <SectionTitle
        eyebrow="01 • DADOS PESSOAIS"
        title="Informações da conta"
        description="Nome exibido no painel e e-mail usado para entrar."
      />
      <form noValidate onSubmit={save} className="flex flex-col gap-[22px]">
        <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
          <TextField
            label="Nome completo *"
            placeholder="Ex: Adriana Oliveira"
            autoComplete="name"
            value={draft.name}
            error={errors.name}
            onChange={(e) => change('name', e.target.value)}
          />
          <TextField
            label="E-mail *"
            type="email"
            placeholder="nome@casaisencheivos.com.br"
            autoComplete="email"
            value={draft.email}
            error={errors.email}
            onChange={(e) => change('email', e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-line pt-[18px]">
          {dirty && (
            <>
              <span className="mr-auto text-[13px] text-muted">Alterações não salvas</span>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDraft({ name, email });
                  setErrors({});
                }}
              >
                Descartar
              </Button>
            </>
          )}
          <Button type="submit" disabled={!dirty || isPending}>
            {isPending ? 'Salvando...' : 'Salvar alterações'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function PasswordForm() {
  const toast = useToast();
  const { mutate: changePassword, isPending } = useChangePassword();
  const [values, setValues] = useState({ current: '', next: '', confirm: '' });
  const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string }>(
    {},
  );
  const [show, setShow] = useState(false);

  const met = PASSWORD_RULES.map(([, test]) => test(values.next));
  const score = values.next ? met.filter(Boolean).length : 0;
  const strength = STRENGTH[score]!;

  const change = (field: keyof typeof values, value: string) => {
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (!values.current) next.current = 'Informe a senha atual.';
    if (values.next.length < 8) next.next = 'Use ao menos 8 caracteres.';
    else if (values.next === values.current)
      next.next = 'A nova senha deve ser diferente da atual.';
    else {
      // Mesma regra da API: maiúscula, minúscula, número e símbolo.
      const parsed = passwordSchema.safeParse(values.next);
      if (!parsed.success) next.next = parsed.error.issues[0]?.message.replace(/!$/, '.');
    }
    if (values.confirm !== values.next) next.confirm = 'As senhas não conferem.';
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    changePassword(
      { currentPassword: values.current, newPassword: values.next },
      {
        onSuccess: () => {
          setValues({ current: '', next: '', confirm: '' });
          setShow(false);
          toast.success('Senha atualizada.');
        },
        onError: (err) =>
          /senha atual/i.test(err.message ?? '')
            ? setErrors({ current: 'Senha atual incorreta.' })
            : toast.error(err.message),
      },
    );
  };

  const type = show ? 'text' : 'password';

  return (
    <Panel className="flex flex-col gap-[22px] p-7">
      <SectionTitle
        eyebrow="02 • SEGURANÇA"
        title="Alterar senha"
        description="Confirme a senha atual e escolha uma nova com pelo menos 8 caracteres."
      />
      <form noValidate onSubmit={save} className="flex flex-col gap-[22px]">
        <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(max(240px,calc(50%-9px)),1fr))]">
          <TextField
            wrapperClassName="col-span-full min-[760px]:max-w-[calc(50%-9px)]"
            label="Senha atual *"
            type={type}
            autoComplete="current-password"
            placeholder="Sua senha atual"
            value={values.current}
            error={errors.current}
            onChange={(e) => change('current', e.target.value)}
          />
          <TextField
            label="Nova senha *"
            type={type}
            autoComplete="new-password"
            placeholder="Mínimo de 8 caracteres"
            value={values.next}
            error={errors.next}
            onChange={(e) => change('next', e.target.value)}
          />
          <TextField
            label="Confirmar nova senha *"
            type={type}
            autoComplete="new-password"
            placeholder="Repita a nova senha"
            value={values.confirm}
            error={errors.confirm}
            onChange={(e) => change('confirm', e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-3 rounded-[14px] border border-line bg-surface-2 p-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-bold tracking-[.1em] text-muted">
              FORÇA DA NOVA SENHA
            </span>
            <span className="text-xs font-bold" style={{ color: strength.color }}>
              {strength.label}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {[1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className="h-1.5 rounded-full bg-bar transition-colors duration-[180ms]"
                style={i <= score ? { background: strength.color } : undefined}
              />
            ))}
          </div>
          <div className="grid gap-x-4 gap-y-2 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
            {PASSWORD_RULES.map(([label], i) => (
              <span
                key={label}
                className={cn(
                  'flex items-center gap-2 text-[13px]',
                  met[i] ? 'text-ink' : 'text-muted',
                )}
              >
                <span
                  className={cn(
                    'flex size-[18px] flex-none items-center justify-center rounded-full border-[1.5px] text-white transition-colors duration-[180ms]',
                    met[i] ? 'border-ok bg-ok' : 'border-line-2',
                  )}
                >
                  {met[i] && <Check className="size-3" strokeWidth={3} />}
                </span>
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-[18px]">
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="min-h-11 text-sm font-semibold text-brand transition-colors hover:text-brand-hover"
          >
            {show ? 'Ocultar senhas' : 'Mostrar senhas'}
          </button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Atualizando...' : 'Atualizar senha'}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function ThemeCard() {
  const { theme, setTheme } = useTheme();

  return (
    <Panel className="flex flex-wrap items-center justify-between gap-5 p-7">
      <div className="min-w-[220px] flex-1">
        <SectionTitle
          eyebrow="03 • PREFERÊNCIAS"
          title="Aparência do painel"
          description="Fica salvo neste navegador."
        />
      </div>
      <div className="grid h-[42px] w-[260px] max-w-full grid-cols-2 gap-1 rounded-full border border-line-2 p-1">
        {(['light', 'dark'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTheme(t)}
            className={cn(
              'rounded-full text-[13px] font-semibold transition-colors',
              theme === t ? 'bg-brand-fill text-white' : 'text-body hover:text-ink',
            )}
          >
            {t === 'light' ? 'Claro' : 'Escuro'}
          </button>
        ))}
      </div>
    </Panel>
  );
}

const ProfilePage = () => {
  const { data: user, isLoading } = useCurrentUser();
  const { theme } = useTheme();
  const { mutate: signOut } = useSignOut();

  if (!user) return <ContentLoader show={isLoading} />;

  const isAdmin = user.role === Role.ADMIN;

  return (
    <div className="flex flex-col gap-5">
      <Helmet>
        <title>Meu perfil | Enchei-vos Admin</title>
      </Helmet>

      <PageHeader
        eyebrow="CONFIGURAÇÕES"
        title="Meu perfil"
        description="Seus dados de acesso, senha e preferências do painel."
      />

      <div className="grid items-start gap-5 min-[1240px]:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="flex flex-col overflow-hidden rounded-[18px] border border-line bg-surface min-[1240px]:sticky min-[1240px]:top-[92px]">
          <div className="relative h-[104px] overflow-hidden bg-brand">
            <img
              src="/assets/about-2.jpg"
              alt=""
              className="absolute inset-0 size-full object-cover opacity-35 mix-blend-luminosity grayscale"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(163,38,43,.2),rgba(80,16,20,.75))]" />
            <span className="absolute top-3.5 right-4 text-[10px] font-bold tracking-[.12em] text-white/85">
              ENCHEI-VOS ADMIN
            </span>
          </div>

          <div className="relative -mt-11 flex flex-col items-center gap-3.5 px-6 pb-6 text-center">
            <ProfileAvatar name={user.name} avatar={user.avatar} />
            <div className="flex max-w-full min-w-0 flex-col gap-1">
              <span className="font-display text-[22px] font-extrabold tracking-[-0.02em] [overflow-wrap:anywhere] text-ink">
                {user.name}
              </span>
              <span className="text-[13px] [overflow-wrap:anywhere] text-muted">
                {user.email}
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <span className="rounded-full border border-brand px-2.5 py-1 text-[11px] font-bold text-brand">
                {isAdmin ? 'Administrador' : 'Padrão'}
              </span>
              <span className="rounded-full bg-ok-tint px-2.5 py-1 text-[11px] font-bold text-ok">
                {user.isActive ? 'Ativo' : 'Inativo'}
              </span>
            </div>
          </div>

          <div className="flex flex-col border-t border-line">
            <Fact
              icon={<ShieldCheck />}
              label="PERFIL DE ACESSO"
              value={
                isAdmin ? 'Administrador — acesso total' : 'Padrão — casais e encontros'
              }
            />
            <Fact icon={<Mail />} label="LOGIN" value={user.email} />
            <Fact
              icon={<LayoutGrid />}
              label="TEMA"
              value={theme === 'dark' ? 'Escuro' : 'Claro'}
            />
          </div>

          <div className="px-6 pt-4 pb-5">
            <button
              type="button"
              onClick={() => signOut()}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-[12px] border border-line-2 text-sm font-semibold text-ink transition-colors duration-[120ms] hover:border-brand hover:text-brand"
            >
              <LogOut className="size-4" /> Sair da conta
            </button>
          </div>
        </aside>

        <div className="flex min-w-0 flex-col gap-5">
          <AccountForm name={user.name} email={user.email} />
          <PasswordForm />
          <ThemeCard />
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
